/**
 * <countdown-timer> — counts down to a fixed end date/time in a given timezone.
 *
 * Attributes:
 *   data-end-date    — target date as "YYYY-MM-DD"
 *   data-end-time    — target time as 24h "HH:MM" (wall-clock time in data-timezone)
 *   data-timezone    — IANA timezone the deadline is defined in (e.g. "Europe/London").
 *                      Falls back to the visitor's local time if missing or invalid.
 *   data-design-mode — "true" inside the theme editor: never hide the block, so the
 *                      merchant can still select and configure it after it expires.
 *
 * Expects descendants with [data-countdown-days|hours|minutes|seconds] to write
 * zero-padded numbers into, and optionally [data-countdown-expired] copy to reveal.
 *
 * The deadline is wall-clock time in the target timezone, so a 23:59 Europe/London
 * deadline lands at 23:59 in London for every visitor, wherever they are. Once it
 * passes, the element hides itself (removed from layout) unless in design mode.
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.designMode = this.dataset.designMode === 'true';
    this.timezone = this.dataset.timezone || undefined;
    this.deadline = this.parseDeadline();
    this.outputs = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };
    this.daysUnit = this.querySelector('[data-countdown-unit="days"]');
    this.hideDaysWhenZero = this.dataset.hideDaysWhenZero === 'true';

    if (this.deadline == null) {
      // No usable deadline — show nothing on the storefront rather than a broken timer.
      if (!this.designMode) this.toggleAttribute('hidden', true);
      return;
    }

    this.update = this.update.bind(this);
    this.update();
    this.timer = setInterval(this.update, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /**
   * Epoch milliseconds for the configured wall-clock deadline, or null if unset/invalid.
   * @returns {number | null}
   */
  parseDeadline() {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec((this.dataset.endDate || '').trim());
    if (!dateMatch) return null;

    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec((this.dataset.endTime || '').trim());
    const [year, month, day] = dateMatch.slice(1).map(Number);
    const hour = timeMatch ? Number(timeMatch[1]) : 23;
    const minute = timeMatch ? Number(timeMatch[2]) : 59;
    if (hour > 23 || minute > 59) return null;

    // Treat the settings as wall-clock time in the target timezone: guess that the
    // wall-clock reading is UTC, then correct by that zone's offset. A second pass
    // catches deadlines that sit on the other side of a DST transition from the guess.
    const wallClock = Date.UTC(year, month - 1, day, hour, minute, 0);
    const firstOffset = this.offsetAt(wallClock);
    if (firstOffset == null) return new Date(year, month - 1, day, hour, minute, 0).getTime();

    let timestamp = wallClock - firstOffset;
    const secondOffset = this.offsetAt(timestamp);
    if (secondOffset != null && secondOffset !== firstOffset) timestamp = wallClock - secondOffset;
    return timestamp;
  }

  /**
   * Offset of the target timezone at a given instant, in milliseconds ahead of UTC.
   * @param {number} timestamp
   * @returns {number | null} null when the timezone is missing or unsupported
   */
  offsetAt(timestamp) {
    if (!this.timezone) return null;
    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timezone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).formatToParts(new Date(timestamp));
      /** @param {string} type */
      const get = (type) => parseInt(parts.find((p) => p.type === type)?.value ?? '', 10);
      let hour = get('hour');
      if (hour === 24) hour = 0; // some engines emit "24" at midnight
      const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
      if (Number.isNaN(asUTC)) return null;
      // Sub-second drift is irrelevant here; zone offsets are whole minutes.
      return asUTC - Math.floor(timestamp / 1000) * 1000;
    } catch (e) {
      return null;
    }
  }

  /**
   * @param {Element | null | undefined} el
   * @param {number} value
   */
  setValue(el, value) {
    const text = String(value).padStart(2, '0');
    if (el && el.textContent !== text) el.textContent = text;
  }

  update() {
    if (this.deadline == null) return;
    let remaining = Math.floor((this.deadline - Date.now()) / 1000);

    if (remaining <= 0) {
      clearInterval(this.timer);
      remaining = 0;
      // Expired: hide on the storefront, but keep it visible (and flagged) in the
      // editor so the block can still be selected and re-dated.
      this.toggleAttribute('hidden', !this.designMode);
      this.toggleAttribute('data-expired', true);
    }

    const days = Math.floor(remaining / 86400);
    this.setValue(this.outputs.days, days);
    this.setValue(this.outputs.hours, Math.floor((remaining % 86400) / 3600));
    this.setValue(this.outputs.minutes, Math.floor((remaining % 3600) / 60));
    this.setValue(this.outputs.seconds, remaining % 60);

    if (this.daysUnit && this.hideDaysWhenZero) {
      this.daysUnit.toggleAttribute('hidden', days === 0);
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
